import { useEffect, useState } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { NOTIFICATION_DURATION_IN_MS } from '@carto/meridian-ds/theme'
import { EditOutlined } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'app/state'
import BuilderTakeOverRequestDialog from 'features/builder/ui/Collaboration/BuilderTakeOverRequestDialog'
import BuilderTakeOverResponseDialog from 'features/builder/ui/Collaboration/BuilderTakeOverResponseDialog'
import { setTakeOverResponseState } from 'features/builder/state/builderSlice'
import { useCurrentCollaborativeMapInfo } from 'features/builder/hooks/collaboration'
import { AlertSeverity, Notification, showNotification } from 'features/notifications/state/notificationSlice'
import { BuilderMode, TakeOverResult, TakeResponseDialogResult } from 'features/builder/types'
import BuilderTakeOverCanceledDialog from 'features/builder/ui/Collaboration/BuilderTakeOverCanceledDialog'
import { navigateWithReload } from 'features/common/utils/navigationUtils'
import HeaderButton from 'features/common/ui/AppTemplate/Header/Partials/HeaderButton'
import { BuilderPaths } from 'features/builder/paths'

const approvedNotification: Notification = {
  msg: 'builder.takeOverRequestDialog.approvedTitle',
  detailsMsg: 'builder.takeOverRequestDialog.approvedMessage',
  formatted: true,
  type: AlertSeverity.Success,
  closeable: true
}

const notificationDenied = (user: string, intl: IntlShape): Notification => ({
  msg: intl.formatMessage({ id: 'builder.takeOverRequestDialog.deniedTitle' }),
  detailsMsg: intl.formatMessage({ id: 'builder.takeOverRequestDialog.deniedMessage' }, { user }),
  formatted: false,
  type: AlertSeverity.Info,
  timeout: NOTIFICATION_DURATION_IN_MS * 2,
  closeable: true
})

const switchedToViewerNotification = {
  msg: 'builder.takeOverResponseDialog.mapSavedTitle',
  detailsMsg: 'builder.takeOverResponseDialog.mapSavedMessage',
  type: AlertSeverity.Success,
  closeable: true
}

export default function BuilderTakeover() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [voluntaryOpen, setVoluntaryOpen] = useState(false)
  const builderMode = useAppSelector((state) => state.builder.builderMode)
  const previewMode = useAppSelector((state) => state.builder.previewMode)
  const takeOverResponseState = useAppSelector((state) => state.builder.takeOverResponseState)
  const { map, userHasEditMapPermission, takeOverNeeded, locked, lockedByEmail, lockedByMe } =
    useCurrentCollaborativeMapInfo()
  const [takeoverCanceledOpen, setTakeoverCanceledOpen] = useState(false)
  const mapId = map?.id
  const collaborative = map?.collaborative
  const inViewerMode = builderMode === BuilderMode.Viewer && !previewMode

  useEffect(() => {
    // we lost lock because of timeout forced takeover
    if (mapId && takeOverResponseState?.active && !lockedByMe) {
      navigateWithReload(BuilderPaths.ViewMap(mapId), { notify: switchedToViewerNotification })
    }
  }, [locked, lockedByMe, takeOverResponseState, mapId])

  const forcedOpen = !inViewerMode && takeOverNeeded

  const handleTakeOverResult = (result: TakeOverResult) => {
    if (!mapId) return

    switch (result) {
      case 'denied': {
        const notification = notificationDenied(lockedByEmail || '', intl)

        if (!forcedOpen) {
          setVoluntaryOpen(false)
          dispatch(showNotification(notification))
        } else {
          navigateWithReload(BuilderPaths.ViewMap(mapId), {
            notify: notification
          })
        }
        break
      }
      case 'cancel':
        if (forcedOpen) {
          navigateWithReload(BuilderPaths.ViewMap(mapId))
        } else {
          setVoluntaryOpen(false)
        }
        break
      case 'locked': {
        if (inViewerMode) {
          navigateWithReload(BuilderPaths.EditMap(mapId), {
            notify: approvedNotification
          })
        } else {
          dispatch(showNotification(approvedNotification))
        }
        break
      }
      case 'conflict':
        // someone else managed to lock map, just notify and keep open
        dispatch(
          showNotification({
            type: AlertSeverity.Info,
            msg: 'Someone else locked the map',
            detailsMsg: 'You may request takeover again',
            formatted: false
          })
        )
    }
  }

  const hadleClickTakeOver = () => {
    if (takeOverNeeded) {
      setVoluntaryOpen(true)
    } else if (mapId) {
      navigateWithReload(BuilderPaths.EditMap(mapId))
    }
  }

  const requestingTakeoverOpen = voluntaryOpen || forcedOpen
  const acceptingTakeoverOpen = !requestingTakeoverOpen && takeOverResponseState?.active

  const handleTakeoverResponse = (result: TakeResponseDialogResult) => {
    if (!mapId) return
    if (result === 'accepted') {
      navigateWithReload(BuilderPaths.ViewMap(mapId), {
        notify: switchedToViewerNotification
      })
    } else if (result === 'canceled') {
      setTakeoverCanceledOpen(true)
      dispatch(setTakeOverResponseState({ ...takeOverResponseState, active: false }))
    } else {
      dispatch(setTakeOverResponseState({ active: false }))
    }
  }

  const editOrtakeOverButtonVisible = userHasEditMapPermission && inViewerMode

  return (
    <>
      {editOrtakeOverButtonVisible && (
        <HeaderButton
          onClick={hadleClickTakeOver}
          startIcon={<EditOutlined />}
          aria-label={takeOverNeeded ? 'takeover-editing' : 'edit-map'}
        >
          <FormattedMessage id={takeOverNeeded ? 'builder.navBar.takeOver' : 'builder.actions.edit'} />
        </HeaderButton>
      )}

      {collaborative && (
        <>
          <BuilderTakeOverRequestDialog
            open={requestingTakeoverOpen}
            mode={forcedOpen ? 'forced' : 'userRequest'}
            onFinished={handleTakeOverResult}
          />
          <BuilderTakeOverResponseDialog open={acceptingTakeoverOpen} onFinished={handleTakeoverResponse} />
          <BuilderTakeOverCanceledDialog
            open={takeoverCanceledOpen}
            onFinished={() => setTakeoverCanceledOpen(false)}
            userEmail={takeOverResponseState?.email || ''}
          />
        </>
      )}
    </>
  )
}
